import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { TAGS_KEYS } from "@/other/constants/TagsKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { TagValue } from "@/bryntum-lib/models/TagValue";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

const ResourceStatusDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [statuses, setStatuses] = useState<TagValue[]>([]);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user])

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.resourceTagValuesStore,
  })

  const loadStatuses = () => {
    if (!project) return;
    const tagValueStore = project.resourceTagValues;
    const statusRecords = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.status, true) || [];
    setStatuses(() => statusRecords);
  }

  useEffect(() => {
    loadStatuses();
    return subscribe(() => {
      loadStatuses();
    })
  }, [subscribe]);

  const isDuplicateStatusName = (name: string, excludeStatusId?: string | number) => {
    if (!project) return false;
    const tagValueStore = project.resourceTagValues;
    const allStatuses = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.status, true) || [];
    return allStatuses.some(s => {
      if (excludeStatusId !== undefined && s.id === excludeStatusId) {
        return false;
      }
      return s.tagValue.toLowerCase() === name.toLowerCase();
    });
  };

  const handleAddItem = (value: string) => {
    if (!value.trim()) {
      return;
    }

    if (isDuplicateStatusName(value)) {
      toast.error('Status name already exists');
      return;
    }

    if (!project) return;
    const tagValueStore = project.resourceTagValues;
    const newStatus = tagValueStore.createRecord({
      tagSet: TAGS_KEYS.status,
      tagValue: value,
    });
    tagValueStore.add(newStatus);
  }

  const handleReorderItems = (movedItem: TagValue, beforeItem: TagValue | null) => {
    if (!project) return;
    const tagValueStore = project.resourceTagValues;
    // @ts-expect-error - tagValue is a TagValue model
    tagValueStore.move(movedItem, beforeItem);
  }

  const handleUpdateStatusName = (status: TagValue, newName: string) => {
    if (!newName.trim()) {
      return false;
    }

    if (isDuplicateStatusName(newName, status.id)) {
      toast.error('Status name already exists');
      return false;
    }

    status.tagValue = newName;
    return true;
  }

  const handleDeleteStatus = (status: TagValue) => {
    if (!project) return;
    const tagValueStore = project.resourceTagValues;
    // @ts-expect-error - status is a TagValue model
    tagValueStore.remove(status);
  }

  return (
    <div className="p-6 flex flex-col max-w-3xl w-full h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <SwivuList.Root<TagValue>
        items={statuses}
        onReorder={handleReorderItems}
        searchConfig={{
          fields: ["tagValue"],
          placeholder: "Search statuses...",
        }}
      >
        <SwivuList.Toolbar />

        <SwivuList.Content<TagValue> emptyMessage="No statuses yet. Create your first status!">
          {(status: TagValue) => (
            <SwivuList.Item value={status.id as string | number}>
              {/* Drag Handle */}
              <SwivuList.ItemHandle disabled={!canEdit} value={status.id as string | number} />

              {/* Status Name */}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <SwivuList.TextField
                  item={status}
                  value={status.tagValue}
                  onUpdate={handleUpdateStatusName}
                  placeholder="Enter status name"
                  disabled={!canEdit}
                  className="flex-1"
                />
              </div>

              {/* Actions */}
              <SwivuList.ItemActions
                disabled={!canEdit}
                onDelete={() => handleDeleteStatus(status)}
              />
            </SwivuList.Item>
          )}
        </SwivuList.Content>

        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={handleAddItem}
          placeholder="Add new status..."
        />
      </SwivuList.Root>
    </div>
  );
};

export default ResourceStatusDetail;
